import React from "react";
import GlobalStyles, { CommonClasses } from "../global/GlobalStyle";
import { MdGavel, MdWarning } from "react-icons/md";

const Terms = () => {
  const GavelIcon = MdGavel as React.FC<React.SVGProps<SVGSVGElement>>;
  const WarningIcon = MdWarning as React.FC<React.SVGProps<SVGSVGElement>>;

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div className="text-center mb-8 py-16">
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
          <GavelIcon className="w-8 h-8 text-white" />
        </div>
        <h1
          className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-4`}
        >
          Terms & Conditions
        </h1>
        <p className={`${CommonClasses.textGray}`}>
          Last updated: {new Date().toLocaleDateString()}
        </p>
      </div>

      <div className={`${GlobalStyles.sectionWrap}`}>
        {/* Important Notice */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <div className={`${CommonClasses.flexStart} gap-3`}>
            <WarningIcon className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                Important Legal Notice
              </p>
              <p
                className={`${CommonClasses.textSm} text-yellow-700 dark:text-yellow-300 mt-1`}
              >
                Please read these terms carefully before using our services. By
                accessing or using OFS Security Ledger, you agree to be bound by
                these terms.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Acceptance of Terms */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              1. Acceptance of Terms
            </h2>
            <p className={`${CommonClasses.textGray} mb-3`}>
              By accessing and using OFS Security Ledger ("the Platform"), you
              accept and agree to be bound by the terms and provision of this
              agreement.
            </p>
          </section>

          {/* Services Description */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              2. Services Description
            </h2>
            <p className={`${CommonClasses.textGray} mb-3`}>
              OFS Security Ledger provides cryptocurrency asset protection
              services including:
            </p>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside mb-3`}
            >
              <li>Digital wallet connection and management</li>
              <li>Cryptocurrency to USD conversion services</li>
              <li>Market protection mechanisms during downturns</li>
              <li>
                Asset growth optimization during favorable market conditions
              </li>
              <li>Secure withdrawal services</li>
            </ul>
          </section>

          {/* User Responsibilities */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              3. User Responsibilities
            </h2>
            <div className="space-y-3">
              <p className={`${CommonClasses.textGray}`}>
                <strong>Eligibility:</strong> You must be at least 18 years old
                and have the legal capacity to enter into binding contracts.
              </p>
              <p className={`${CommonClasses.textGray}`}>
                <strong>Account Security:</strong> You are responsible for
                maintaining the confidentiality of your login credentials and
                for all activities under your account.
              </p>
              <p className={`${CommonClasses.textGray}`}>
                <strong>Accurate Information:</strong> You agree to provide
                accurate, current, and complete information during registration.
              </p>
              <p className={`${CommonClasses.textGray}`}>
                <strong>Compliance:</strong> You agree to comply with all
                applicable laws and regulations regarding cryptocurrency in your
                jurisdiction.
              </p>
            </div>
          </section>

          {/* Risk Disclosure */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              4. Risk Disclosure
            </h2>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <WarningIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p
                  className={`${CommonClasses.textSm} text-red-700 dark:text-red-300`}
                >
                  Cryptocurrency investments carry substantial risk. The value
                  of cryptocurrencies can be volatile and investors may lose
                  their entire investment.
                </p>
              </div>
            </div>
            <p className={`${CommonClasses.textGray} mb-3`}>
              While OFS Security Ledger employs advanced protection mechanisms,
              we cannot guarantee profits or complete protection from losses.
              Past performance is not indicative of future results.
            </p>
          </section>

          {/* Fees and Charges */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              5. Fees and Charges
            </h2>
            <p className={`${CommonClasses.textGray} mb-3`}>
              Our fee structure includes:
            </p>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside mb-3`}
            >
              <li>Deposit processing fees: 0.5%</li>
              <li>Withdrawal fees: 1%</li>
              <li>Protection service fee: 0.25% monthly on protected assets</li>
              <li>Network fees: Actual blockchain network costs</li>
            </ul>
            <p className={`${CommonClasses.textGray}`}>
              All fees are subject to change with 30 days notice to users.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              6. Intellectual Property
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              All content on the Platform, including text, graphics, logos, and
              software, is the property of OFS Security Ledger and protected by
              intellectual property laws.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              7. Limitation of Liability
            </h2>
            <p className={`${CommonClasses.textGray} mb-3`}>
              To the maximum extent permitted by law, OFS Security Ledger shall
              not be liable for:
            </p>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside mb-3`}
            >
              <li>Any indirect, incidental, or consequential damages</li>
              <li>Loss of profits or investment capital</li>
              <li>Damages resulting from market volatility</li>
              <li>Security breaches resulting from user negligence</li>
              <li>Force majeure events including regulatory changes</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              8. Termination
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              We reserve the right to suspend or terminate your account at our
              discretion, with or without cause, with reasonable notice except
              in cases of fraud or security concerns.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              9. Governing Law
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              These terms shall be governed by and construed in accordance with
              the laws of the jurisdiction where OFS Security Ledger is
              incorporated, without regard to its conflict of law provisions.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              10. Changes to Terms
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              We reserve the right to modify these terms at any time. Continued
              use of the Platform after changes constitutes acceptance of the
              modified terms.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              11. Contact Information
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              For questions about these Terms & Conditions, please contact us
              at:
              <br />
              <strong>Support:</strong> support@ofssecledger.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;
